import { S3Attachment } from '../../../types';
import { DetailViewCard } from '../CardWithTitle';
import { AttachmentDownloadBtn } from '../AttachmentDownloadBtn';
import { AttachmentDeleteBtn } from '../DeleteBtn/AttachmentDeleteBtn';
import { DocumentPreviewer } from '../DocumentPreviewer';
import { PermissibleRender } from '../PermissibleRender';
import { useEffect, useState } from 'react';
import { Permission } from '../../../store/GeneralStore';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import {
  downloadS3Attachment,
  get3AttachmentSubject,
  getS3AttachmentsLoadingSubject,
  getS3AttachmentsUploadingSubject,
  useS3Attachments
} from '../../../store/S3Attachment';
import { AttachmentTableCTA } from '../AttachmentCTA/AttachmentTableCTA';

export function S3AttachmentTable({ id, entityType }: { id: string; entityType: string }) {
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [uploadedAttachments, setUploadedAttachments] = useState<S3Attachment[]>([]);
  const { deleteS3Attachment, getS3Attachments, updateS3Attachments, setS3EntityId, setS3EntityType } =
    useS3Attachments();

  useEffect(() => {
    const attachmentLoadingSub = getS3AttachmentsLoadingSubject().subscribe((loading) => setIsFetching(loading));
    const attachmentUploadingSub = getS3AttachmentsUploadingSubject().subscribe((uploading) => setUploading(uploading));

    return () => {
      if (attachmentLoadingSub) attachmentLoadingSub.unsubscribe();
      if (attachmentUploadingSub) attachmentUploadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (id) {
      setS3EntityId(id);
      setS3EntityType('OutsideInventory');
      getS3Attachments(id, entityType);
    }

    const attachmentSub = get3AttachmentSubject().subscribe(({ attachments }) => {
      setUploadedAttachments(attachments.filter((a) => a.entity_id === id));
    });

    return () => {
      if (attachmentSub) attachmentSub.unsubscribe();
    };
  }, [id]);

  const handleAddAttachments = (files: File[]) => {
    updateS3Attachments(files);
    setCollapsed(false);
  };

  const cardActions = () => {
    return (
      <PermissibleRender requiredPermissions={[Permission.ADD_ATTACHMENTS_INVENTORY]}>
        <AttachmentTableCTA addAttachments={handleAddAttachments} />
      </PermissibleRender>
    );
  };

  return (
    <DetailViewCard
      cardActions={cardActions()}
      collapsed={collapsed}
      enableCollapse
      title={`Attachments (${uploadedAttachments?.length || 0})`}
      childrenWrapperClass="overflow-x-auto">
      <>
        {(isFetching || uploading) && <ComponentSpinner />}
        {!isFetching && !uploading && !uploadedAttachments.length && <div className="text-center">No Attachments</div>}
        {!isFetching && !uploading && !!uploadedAttachments.length && (
          <table className="w-full [&_tr]:border-y">
            <thead>
              <tr className="text-indigo-mid text-xs text-left [&>*]:font-normal [&>*]:p-2">
                <th className="border-r">File Name</th>
                <th>Upload Date</th>
                <th colSpan={2}>Actions</th>
              </tr>
            </thead>
            <tbody className="overflow-auto">
              {uploadedAttachments
                ?.filter((a) => a.entity_id === id)
                ?.map((attachment) => (
                  <tr
                    key={`${attachment.filename}-${attachment.id}`}
                    className="text-black text-sm text-left font-normal [&>*]:p-[0.65rem]">
                    <td className="border-r min-w-[10rem]">
                      <DocumentPreviewer
                        id={attachment.id}
                        name={attachment.filename}
                        downloadAttachment={downloadS3Attachment}
                        isS3={true}
                      />
                      {attachment.filename}
                    </td>
                    <td className="border-r">{new Date(attachment.created_at).toLocaleDateString()}</td>
                    <td className="border-r">
                      <AttachmentDownloadBtn
                        attachmentId={attachment.id}
                        name={attachment.filename}
                        downloadAttachment={downloadS3Attachment}
                      />
                    </td>
                    <td>
                      <PermissibleRender requiredPermissions={[Permission.ADD_CASES_ATTACHMENTS]}>
                        <AttachmentDeleteBtn
                          attachmentId={attachment.id}
                          deleteAttachment={deleteS3Attachment}
                        />
                      </PermissibleRender>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </>
    </DetailViewCard>
  );
}
